import * as React from 'react';
import { useQuery, useQueryClient } from 'react-query';

import { FEATURE_API_ROUTE } from '@/api-routes';
import { axiosInstance } from '@/axios';

const QUERY_KEYS = ['feature_config'];

const FEATURES = {
  /** core_features */
  assistants: 'assistants',

  ask_needl_upload: 'ask_needl_upload',

  search: 'search',

  needl_report: 'needl_report',
  needl_report_credit_report: 'needl_report_credit_report',
  needl_report_maintenance_coverage: 'needl_report_maintenance_coverage',
  needl_report_initiation_coverage: 'needl_report_initiation_coverage',
  needl_report_drhp_report: 'needl_report_drhp_report',
  needl_report_annual_report: 'needl_report_annual_report',
  needl_report_icra_credit_report: 'needl_report_icra_credit_report',
  needl_report_unlisted_icra_credit_report:
    'needl_report_unlisted_icra_credit_report',

  my_feeds: 'my_feeds',
  feed_from_scratch: 'feed_from_scratch',
  feed_from_ai: 'feed_from_ai',
  feed_from_templates: 'feed_from_templates',

  /** data_management */
  my_data: 'my_data',
  clipped_reports: 'clipped_reports',
  notebooks: 'notebooks',
  needldrive: 'needldrive',
  portfolio_watchlist: 'portfolio_watchlist',

  /** connected_sources */
  apps: 'apps',
  apps_ms_teams: 'apps_ms_teams',
  apps_gmail: 'apps_gmail',
  apps_outlook: 'apps_outlook',
  apps_twitter: 'apps_twitter',
  apps_google_drive: 'apps_google_drive',
  apps_evernote: 'apps_evernote',
  apps_slack: 'apps_slack',
  apps_sharepoint: 'apps_sharepoint',
  apps_box: 'apps_box',
  apps_onedrive: 'apps_onedrive',
  apps_dropbox: 'apps_dropbox',
  apps_onenote: 'apps_onenote',
  apps_azure_blob: 'apps_azure_blob',
  apps_telegram: 'apps_telegram',

  websites: 'websites',

  capital_markets: 'capital_markets',
  capital_markets_indian: 'capital_markets_indian',
  capital_markets_us: 'capital_markets_us',
  capital_markets_nz: 'capital_markets_nz',

  /** collaboration */
  comments: 'comments',
  group_feeds: 'group_feeds',
  suggest_sources: 'suggest_sources',
  publish_feeds: 'publish_feeds',
  copy_feed_template: 'copy_feed_template',
  exit_feed: 'exit_feed',

  /** others */
  explore_menu: 'explore_menu',
  needl_feeds: 'needl_feeds',
  document_public_share: 'document_public_share',
  market_intelligence_developer_options:
    'market_intelligence_developer_options',

  /** assistant_types */
  portfolio_summarization_assistant: 'portfolio_summarization_assistant',
  portfolio_summarization_assistant_output_type_whatsapp:
    'portfolio_summarization_assistant_output_type_whatsapp',
  portfolio_summarization_assistant_output_type_email:
    'portfolio_summarization_assistant_output_type_email',
  portfolio_summarization_assistant_output_type_azure_blob:
    'portfolio_summarization_assistant_output_type_azure_blob',

  early_warning_signal_assistant: 'early_warning_signal_assistant',
  early_warning_signal_assistant_output_type_whatsapp:
    'early_warning_signal_assistant_output_type_whatsapp',
  early_warning_signal_assistant_output_type_email:
    'early_warning_signal_assistant_output_type_email',
  early_warning_signal_assistant_output_type_azure_blob:
    'early_warning_signal_assistant_output_type_azure_blob',

  feed_summary_assistant: 'feed_summary_assistant',
  feed_summary_assistant_output_type_email:
    'feed_summary_assistant_output_type_email',
  feed_summary_assistant_output_type_azure_blob:
    'feed_summary_assistant_output_type_azure_blob',

  market_announcement_signal: 'market_announcement_signal',
  market_announcement_signal_output_type_email:
    'market_announcement_signal_output_type_email',
  market_announcement_signal_output_type_azure_blob:
    'market_announcement_signal_output_type_azure_blob',

  market_rumour_signal_assistant: 'market_rumour_signal_assistant',
  market_rumour_signal_assistant_output_type_email:
    'market_rumour_signal_assistant_output_type_email',
  market_rumour_signal_assistant_output_type_whatsapp:
    'market_rumour_signal_assistant_output_type_whatsapp',
  market_rumour_signal_assistant_output_type_azure_blob:
    'market_rumour_signal_assistant_output_type_azure_blob',
  needl_report_assistant: 'needl_report_assistant',

  /** data_actions */
  doc_download: 'doc_download',
  doc_delete: 'doc_delete',
  feed_filters: 'feed_filters',
  feed_sorting: 'feed_sorting',
  doc_share: 'doc_share',

  /** display_options */
  doc_full_screen_view: 'doc_full_screen_view',
  doc_open_in_source: 'doc_open_in_source',
  doc_open_in_new_tab: 'doc_open_in_new_tab',
  doc_overlay: 'doc_overlay',
  category_tabs: 'category_tabs',
  doc_mark_as_read: 'doc_mark_as_read',
  save_search: 'save_search',
  website_priority: 'website_priority',
  website_unfollow: 'website_unfollow',
  doc_hide_source: 'doc_hide_source',
  switch_old_ui: 'switch_old_ui',

  /** managers */
  feed_manager: 'feed_manager',
  needl_storage: 'needl_storage',

  /** notification_and_alerts */
  notification: 'notification',
  app_synced_panel: 'app_synced_panel',

  /** support_and_information */
  help_center: 'help_center',
  whats_new: 'whats_new',
  submit_feedback: 'submit_feedback',
  terms_of_service: 'terms_of_service',

  /** knowledge_management */
  knowledge_management: 'knowledge_management',

  /** report_automation */
  report_automation: 'report_automation',

  /** market_intelligence */
  market_intelligence: 'market_intelligence',

  /** auto_dd */
  auto_dd: 'auto_dd',
};

export const useConfigUI = () => {
  return useQuery(QUERY_KEYS, fetchFeaturesConfig, {
    select: (response) => response.features,
    staleTime: Infinity,
  });
};

export const useFeatureConfigUI = (feature: FeaturesConfigKey) => {
  const { data, isLoading } = useConfigUI();
  return React.useMemo(() => {
    return {
      isLoading,
      feature: data?.[feature] ?? false,
    };
  }, [isLoading, data, feature]);
};

export const usePrefetchConfigUI = () => {
  const queryClient = useQueryClient();

  React.useEffect(() => {
    queryClient.prefetchQuery(QUERY_KEYS, {
      queryFn: fetchFeaturesConfig,
      staleTime: Infinity,
    });
  }, [queryClient]);
};

const fetchFeaturesConfig = async () => {
  const { data } = await axiosInstance.get<Response>(
    FEATURE_API_ROUTE.config()
  );

  return data;
};

export type FeaturesConfigKey = keyof typeof FEATURES;

export type FeaturesConfigType = {
  [k in FeaturesConfigKey]: boolean;
};

type Response = {
  features: FeaturesConfigType;
};
